import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";

const PORTRAIT = "/assets/seyoun-portrait.jpg";

const FACETS = [
  {
    n: "1",
    title: "Curatorial Philosophy",
    body: "Curating is an act of care. Seyoun approaches exhibition-making as world-building: sequencing works, space, and audience experience so that a show unfolds like a story rather than a survey. Always in service of the artists and the communities the work speaks to.",
  },
  {
    n: "2",
    title: "The House of Ego",
    body: "The House of Ego grew out of a simple question: what becomes possible when we set the ego aside? It is the platform through which exhibitions, community programming, and artist advocacy come together as one continuous practice.",
  },
  {
    n: "3",
    title: "Community-Building",
    body: "Seyoun builds rooms, literal and figurative, where artists and audiences can be in honest conversation, because community is what makes the work matter.",
  },
  {
    n: "4",
    title: "Beyond the Studio",
    body: "Outside of the gallery, Seyoun loves to travel and is a competitive Judoka, two practices that, like art, are built on discipline, presence, and the willingness to be thrown off balance and find your footing again.",
  },
  {
    n: "5",
    title: "Artistic Philosophy",
    body: "Each body of work begins as a personal excavation and becomes an invitation for the viewer to locate their own story inside it: art as mirror, not spectacle.",
  },
];

export default function AboutPage() {
  useSEO(
    "About Seyoun Taylor · The House of Ego",
    "Seyoun Taylor is a surrealistic artist, spiritual architect, and curator based in Dallas–Fort Worth, inspiring healing and awareness through storytelling."
  );

  return (
    <main id="main-content" data-testid="about-page">
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-20 grid gap-14 lg:grid-cols-2 lg:items-end">
        <div>
          <Reveal>
            <p className="eyebrow">About the Artist</p>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight text-ink leading-[1.05]">
              Hi, my name is Seyoun{" "}
              <span className="text-neutral-400 text-3xl md:text-5xl align-middle">(Say-on).</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg md:text-xl leading-relaxed text-neutral-600 max-w-xl">
              I am a surrealistic artist, spiritual architect, but a curator that inspires healing and
              awareness through storytelling.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 text-sm uppercase tracking-[0.25em] text-neutral-400">
              Dallas–Fort Worth, Texas · Working everywhere
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <figure className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-flame" aria-hidden="true" />
            <img
              src={PORTRAIT}
              alt="Seyoun Taylor, artist and curator of The House of Ego"
              data-testid="about-portrait"
              className="relative w-full aspect-[4/5] object-cover bg-stone2"
            />
          </figure>
        </Reveal>
      </section>

      <section className="rule" data-testid="about-facets-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">The Practice</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Five ways of seeing
            </h2>
          </Reveal>
          <div className="mt-14">
            {FACETS.map((f, i) => (
              <Reveal key={f.n} delay={i * 0.06}>
                <div className="grid md:grid-cols-[80px_280px_1fr] gap-4 md:gap-10 py-10 border-t border-neutral-200 last:border-b items-baseline">
                  <p className="font-serif text-4xl text-royal">{f.n}</p>
                  <h3 className="font-serif text-2xl text-ink">{f.title}</h3>
                  <p className="text-neutral-600 leading-relaxed max-w-2xl">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-white" data-testid="about-quote-section">
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28 text-center">
          <Reveal>
            <blockquote className="font-serif text-2xl md:text-4xl leading-snug tracking-tight">
              “Through my work, I project a unique essence that gives viewers permission to shift and
              evolve alongside me.”
            </blockquote>
            <p className="mt-6 text-sm text-neutral-400">Seyoun Taylor</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 text-center" data-testid="about-cta-section">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-ink max-w-2xl mx-auto">
            See where the practice has already built rooms.
          </h2>
          <Link
            to="/curatorial"
            data-testid="about-cta-curatorial"
            className="mt-8 inline-flex items-center gap-2 bg-royal text-white px-8 py-4 text-sm tracking-wide hover:bg-royal-deep transition-colors"
          >
            Curatorial Practice <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
